import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { Button, Callout, Card } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import UserLayout from '@/components/Layout/UserLayout';
import TradeLogicRuleEditor from '@/features/tradeLogicAuthoring/components/TradeLogicRuleEditor';
import { parseTradeLogicSeedCondition, TRADE_LOGIC_SEED_CONDITION_PARAM_KEY } from '@/features/tradeLogicAuthoring/seedCondition';
import {
    buildTradeLogicReturnNavigationTarget,
    resolveTradeLogicEditorContext,
    resolveTradeLogicEditorLoadSource,
    sanitizeTradeLogicReturnTo
} from '@/features/tradeLogicAuthoring/tradeLogicNavigation';

const parseDefinitionId = (raw: string | undefined): number | null => {
    if (!raw || raw === 'new') return null;
    const value = Number(raw);
    return Number.isInteger(value) && value > 0 ? value : null;
};

const parseKind = (raw: string | undefined): 'entry' | 'exit' | null => {
    if (raw === 'entry' || raw === 'exit') return raw;
    return null;
};

const TradeLogicEditorPage = () => {
    const { kind: rawKind, id: rawId } = useParams<{ kind: string; id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const kind = parseKind(rawKind);
    const isNew = rawId === 'new';
    const definitionId = parseDefinitionId(rawId);
    const hasInvalidRuleId = !isNew && definitionId === null;
    const returnTo = sanitizeTradeLogicReturnTo(searchParams.get('returnTo'));
    const attachKind = searchParams.get('attachKind');
    const ideaLabVariantId = parseDefinitionId(searchParams.get('ideaLabVariantId') ?? undefined);
    const ideaLabVariantQuery = useAppQuery(
        {
            ...trpc.ideaLab.getVariant.queryOptions({ variantId: ideaLabVariantId ?? 0 }),
            enabled: ideaLabVariantId !== null
        },
        { fallbackErrorMessage: 'Failed to load the Idea Lab variant' }
    );
    const ideaLabVariant = ideaLabVariantQuery.data ?? null;
    const ideaLabEditorTarget = useMemo(
        () =>
            ideaLabVariant && ideaLabVariantId !== null
                ? {
                      variantId: ideaLabVariant.id,
                      studyId: ideaLabVariant.studyId,
                      label: ideaLabVariant.label,
                      payload: ideaLabVariant.authoringPayload
                  }
                : null,
        [ideaLabVariant, ideaLabVariantId]
    );

    const loadSource = resolveTradeLogicEditorLoadSource({
        routeDefinitionId: definitionId,
        searchParams
    });

    const seedConditionRaw = searchParams.get(TRADE_LOGIC_SEED_CONDITION_PARAM_KEY);
    const seedConditionInvalid = parseTradeLogicSeedCondition(seedConditionRaw).status === 'invalid';

    const editorContext = resolveTradeLogicEditorContext({
        attachKind,
        kind,
        returnTo
    });

    const backPath = returnTo ?? '/user/tradeLogic';
    const backLabel = backPath.startsWith('/user/tradingStrategy/')
        ? 'Back to strategy'
        : backPath.startsWith('/user/ideaLab/')
          ? 'Back to Idea Lab'
          : 'Back to library';

    const navigateToReturnTarget = () => navigate(backPath);

    const applySavedRuleToReturnTarget = useMemo(
        () => (result: { definitionId: number; version: number; versionId: number }) => {
            const navigationTarget = buildTradeLogicReturnNavigationTarget({
                attachKind,
                kind,
                result,
                returnTo
            });

            if (navigationTarget === null) return false;

            void navigate(navigationTarget, { replace: true });

            return true;
        },
        [attachKind, kind, navigate, returnTo]
    );

    if (!kind) {
        return (
            <UserLayout containerSize="1400px" title="Trade Logic — Project 314">
                <Card className="mb-6">
                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-zinc-100">Unknown rule kind</h3>
                        <p className="text-sm text-zinc-400">Use entry or exit in the URL.</p>
                        <Button variant="secondary" onClick={navigateToReturnTarget}>
                            {backLabel}
                        </Button>
                    </div>
                </Card>
            </UserLayout>
        );
    }

    if (hasInvalidRuleId) {
        return (
            <UserLayout containerSize="1400px" title="Trade Logic — Project 314">
                <Card className="mb-6">
                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-zinc-100">Invalid rule ID</h3>
                        <p className="text-sm text-zinc-400">Use a numeric rule ID or `new`.</p>
                        <Button variant="secondary" onClick={navigateToReturnTarget}>
                            {backLabel}
                        </Button>
                    </div>
                </Card>
            </UserLayout>
        );
    }

    if (ideaLabVariantId !== null) {
        if (ideaLabVariantQuery.isLoading) {
            return (
                <UserLayout containerSize="1400px" title="Idea Lab rules — Project 314">
                    <DataLoadingSpinner text="Loading the variant's rules..." />
                </UserLayout>
            );
        }

        if (!ideaLabVariant || ideaLabVariant.lifecycle !== 'draft' || kind !== 'entry') {
            return (
                <UserLayout containerSize="1400px" title="Idea Lab rules — Project 314">
                    <Card className="mb-6">
                        <div className="space-y-3">
                            <h3 className="text-lg font-semibold text-zinc-100">This variant cannot be edited here</h3>
                            <p className="text-sm text-zinc-400">
                                {!ideaLabVariant
                                    ? 'The Idea Lab variant was not found.'
                                    : kind !== 'entry'
                                      ? 'Idea Lab variants are entry rules.'
                                      : `Variant "${ideaLabVariant.label}" is ${ideaLabVariant.lifecycle}; only draft variants are editable. Make a variation of it to get an editable copy.`}
                            </p>
                            <Button variant="secondary" onClick={navigateToReturnTarget}>
                                {backLabel}
                            </Button>
                        </div>
                    </Card>
                </UserLayout>
            );
        }
    }

    const title =
        ideaLabVariant && ideaLabVariantId !== null
            ? `Idea Lab rules: ${ideaLabVariant.label}`
            : isNew && loadSource?.mode === 'copyFromPublished'
              ? editorContext.mode === 'strategy'
                  ? `Fork ${kind} rules`
                  : `Revise ${kind} rules`
              : isNew
                ? `New ${kind} rules`
                : `${kind === 'entry' ? 'Entry' : 'Exit'} rules`;

    return (
        <UserLayout containerSize="1400px" title={`${title} — Project 314`}>
            <div className="mb-4 flex items-center justify-between">
                <Button size="sm" variant="outline-dark" onClick={navigateToReturnTarget}>
                    ← {backLabel}
                </Button>
            </div>
            {seedConditionInvalid && (
                <Callout className="mb-4" title="Seeded condition could not be read" variant="warning">
                    The seedCondition URL parameter is not a valid veto condition, so nothing was pre-added to the rules. Add the regime
                    veto condition manually.
                </Callout>
            )}
            <TradeLogicRuleEditor
                definitionId={definitionId}
                ideaLabVariant={ideaLabEditorTarget}
                initialVersion={loadSource?.version ?? null}
                kind={kind}
                saveContext={editorContext}
                onSavedToIdeaLab={() => {
                    void navigate(backPath, { replace: true });
                }}
                seedConditionRaw={seedConditionRaw}
                sourceDefinitionId={loadSource?.definitionId ?? null}
                sourceMode={loadSource?.mode ?? null}
                onSaved={(result, meta) => {
                    if (editorContext.mode === 'strategy' && editorContext.strategyId !== null && meta?.appliedToStrategy === true) {
                        void navigate(backPath, { replace: true });
                        return;
                    }

                    if (applySavedRuleToReturnTarget(result)) {
                        return;
                    }

                    if (isNew && result.definitionId) {
                        void navigate(`/user/tradeLogic/${kind}/${result.definitionId}`, { replace: true });
                    }
                }}
            />
        </UserLayout>
    );
};

export default TradeLogicEditorPage;
